import React, { useState, useEffect, useCallback } from 'react';
import { X, Check, AlertTriangle, Save, Ban } from 'lucide-react';
import { Project, VWBAAnalysis, updateProject, associateFactSheetWithProject } from '../services/api';

interface FactsheetComparisonModalProps {
  extractedData: VWBAAnalysis;
  existingProject: Project;
  factSheetId: string;
  onClose: () => void;
  onResolved: () => void;
}

interface ComparisonField {
  key: string;
  name: string;
  factSheetValue: string;
  systemValue: string;
  match: boolean;
}

const FactsheetComparisonModal: React.FC<FactsheetComparisonModalProps> = ({
  extractedData,
  existingProject,
  factSheetId,
  onClose,
  onResolved,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handle escape key to close modal
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [handleKeyDown]);

  // Extract values from extractedData
  const getExtractedValue = (key: string): string => {
    const data = extractedData.extractedData || {};
    if (key === 'name') return extractedData.projectName || '';
    if (key === 'basin') return data.basin || data.location || '';
    if (key === 'category') return data.category || '';
    if (key === 'volumetricWaterBenefit') return data.volumetricWaterBenefit || data.benefitVolume || '';
    if (key === 'costShare') return data.costShare || '';
    if (key === 'affectedFieldImpact') return data.affectedFieldImpact || '';
    if (key === 'quantificationMethod') return data.quantificationMethod || '';
    if (key === 'benefitEndDate') return data.benefitEndDate || '';
    return '';
  };

  // Build comparison fields
  const fields: ComparisonField[] = [
    {
      key: 'basin',
      name: 'Location/Basin',
      factSheetValue: getExtractedValue('basin'),
      systemValue: existingProject.basin || '',
      match:
        getExtractedValue('basin').toLowerCase() ===
        (existingProject.basin || '').toLowerCase(),
    },
    {
      key: 'volumetricWaterBenefit',
      name: 'Volumetric Water Benefit',
      factSheetValue: getExtractedValue('volumetricWaterBenefit'),
      systemValue: existingProject.volumetricWaterBenefit || '',
      match: getExtractedValue('volumetricWaterBenefit') === (existingProject.volumetricWaterBenefit || ''),
    },
    {
      key: 'costShare',
      name: 'Cost-Share',
      factSheetValue: getExtractedValue('costShare'),
      systemValue: existingProject.costShare || '',
      match: getExtractedValue('costShare') === (existingProject.costShare || ''),
    },
    {
      key: 'quantificationMethod',
      name: 'Quantification Method',
      factSheetValue: getExtractedValue('quantificationMethod'),
      systemValue: existingProject.quantificationMethod || '',
      match:
        getExtractedValue('quantificationMethod') ===
        (existingProject.quantificationMethod || ''),
    },
    {
      key: 'benefitEndDate',
      name: 'Benefit Claim End Date',
      factSheetValue: getExtractedValue('benefitEndDate'),
      systemValue: existingProject.benefitEndDate || '',
      match: getExtractedValue('benefitEndDate') === (existingProject.benefitEndDate || ''),
    },
  ];

  const hasMismatch = fields.some((f) => !f.match);

  // Editable values for the right column (new factsheet data) — initialized with ALL extracted values
  const [editedValues, setEditedValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    fields.forEach((field) => {
      initial[field.key] = field.factSheetValue;
    });
    return initial;
  });

  const handleValueChange = (key: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleResolve = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build previousYearData from existing project
      const previousYearData: { volumetricWaterBenefit: string; costShare?: string } = {
        volumetricWaterBenefit: existingProject.volumetricWaterBenefit || '',
      };
      if (existingProject.costShare) {
        previousYearData.costShare = existingProject.costShare;
      }

      // Build update payload — user resolved differences, project is clean
      // Mark as historic since it now has year-over-year data
      const updatePayload: Record<string, unknown> = {
        status: 'clean',
        type: 'historic',
        hasComparison: true,
        flags: [],
        previousYearData,
      };

      // Add all edited values (the new factsheet data) to the payload
      Object.entries(editedValues).forEach(([key, value]) => {
        if (value) {
          updatePayload[key] = value;
        }
      });

      // Update the project with new factsheet values
      await updateProject(existingProject.id, updatePayload as Parameters<typeof updateProject>[1]);

      // Associate the factsheet with the project
      await associateFactSheetWithProject(factSheetId, existingProject.id);

      onResolved();
    } catch (err) {
      console.error('Error resolving project:', err);
      setError('Failed to resolve inconsistencies. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkNonEligible = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await updateProject(existingProject.id, {
        status: 'non-eligible',
      });

      // Associate the factsheet with the project
      await associateFactSheetWithProject(factSheetId, existingProject.id);

      onResolved();
    } catch (err) {
      console.error('Error marking project as non-eligible:', err);
      setError('Failed to mark project as non-eligible. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="factsheet-comparison-modal-title"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2
              id="factsheet-comparison-modal-title"
              className="text-xl font-bold text-gray-900"
            >
              Matching Project Found
            </h2>
            <p className="text-gray-600 mt-1">
              A project with the same name and location already exists. Review and confirm the new data.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Project Name */}
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-700">
              <strong>Project:</strong> {existingProject.name}
            </p>
          </div>

          {/* Comparison Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* LEFT: Existing Project Data (read-only) */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Existing Project Data
              </h3>
              <div className="space-y-3" role="list" aria-label="Existing project values">
                {fields.map((field, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      field.match
                        ? 'border-green-200 bg-green-50'
                        : 'border-red-200 bg-red-50'
                    }`}
                    role="listitem"
                  >
                    <div className="text-xs text-gray-500 uppercase">{field.name}</div>
                    <div className="text-sm font-medium text-gray-900 mt-1">
                      {field.systemValue || '(not set)'}
                    </div>
                    <div
                      className={`flex items-center gap-1 mt-2 text-xs ${
                        field.match ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {field.match ? (
                        <Check className="w-3 h-3" aria-hidden="true" />
                      ) : (
                        <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                      )}
                      <span>{field.match ? 'Match' : 'Mismatch'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT: New Factsheet (Extracted) — editable */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                New Factsheet (Extracted)
              </h3>
              <div className="space-y-3" role="list" aria-label="New factsheet values">
                {fields.map((field, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      field.match
                        ? 'border-green-200 bg-green-50'
                        : 'border-cyan-200 bg-cyan-50'
                    }`}
                    role="listitem"
                  >
                    <div className="text-xs text-gray-500 uppercase">{field.name}</div>
                    <input
                      type="text"
                      value={editedValues[field.key] ?? field.factSheetValue}
                      onChange={(e) => handleValueChange(field.key, e.target.value)}
                      className={`mt-1 w-full px-2 py-1 text-sm border rounded focus:ring-cyan-500 focus:border-cyan-500 ${
                        field.match ? 'border-green-300 bg-green-50' : 'border-cyan-300 bg-white'
                      }`}
                    />
                    <div
                      className={`flex items-center gap-1 mt-2 text-xs ${
                        field.match ? 'text-green-600' : 'text-cyan-600'
                      }`}
                    >
                      {field.match ? (
                        <Check className="w-3 h-3" aria-hidden="true" />
                      ) : (
                        <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                      )}
                      <span>{field.match ? 'Match' : 'Changed'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Action Required */}
          {hasMismatch && (
            <div
              className="p-4 rounded-lg bg-amber-50 border-l-4 border-amber-500"
              role="alert"
            >
              <h4 className="font-semibold text-amber-800">Action Required</h4>
              <p className="text-sm text-amber-700 mt-1">
                Discrepancies detected between existing project and new factsheet data. Review the
                values on the right and click &quot;Resolve & Save&quot; to update the project with the new data.
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-4 rounded-lg bg-red-50 border-l-4 border-red-500" role="alert">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between pt-4 border-t border-gray-200">
            <button
              onClick={handleMarkNonEligible}
              className="px-4 py-2 text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-2"
              disabled={isSubmitting}
            >
              <Ban className="w-4 h-4" />
              {isSubmitting ? 'Processing...' : 'Mark Non-Eligible'}
            </button>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleResolve}
                className="px-4 py-2 text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors flex items-center gap-2"
                disabled={isSubmitting}
              >
                <Save className="w-4 h-4" />
                {isSubmitting ? 'Saving...' : 'Resolve & Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FactsheetComparisonModal;
